import React, { useState, useEffect, useCallback } from "react";
import useStyles from "./style";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Slide,
  DialogActions,
  Typography,
  Button,
  IconButton,
  Divider,
  Box,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import { useFormik } from "formik";
import { useDropzone } from "react-dropzone";
import * as yup from "yup";

// Redux
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch, useSelector } from "react-redux";

// Services
import VehicleApi from "Services/Vehicle";
import ServicetypeApi from "Services/Servicetype";
import DriverApi from "Services/Driver";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const VehicleCreate = (props) => {
  const { openDialog, handleClose } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state?.AuthReducer?.user || {});
  

  const [vehicleImage, setVehicleImage] = useState(null);
  const [serviceTypes, setServiceTypes] = useState([]);
  // Fetch service types
  const fetchServiceTypes = useCallback(() => {
    ServicetypeApi.getServicesList({ page: 1, limit: "unlimited" }).then(
      (res) => {
        if (res?.status === 200) {
          setServiceTypes(res?.data?.data?.data || []);
        }
      },
      () => {}
    );
  }, []);

  useEffect(() => {
    if (openDialog) {
      fetchServiceTypes();
    }
  }, [openDialog, fetchServiceTypes]);

  // Max file size 5 MB
  const maxFileSize = 5242880;
  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxFiles: 1,
    accept: "image/*",
    validator: (file) => {
      if (file.size > maxFileSize) {
        alert(`File size is larger than ${formatBytes(maxFileSize)}`);
        return { code: "file-too-large", message: "File too large" };
      }
      return null;
    },
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const reader = new FileReader();
        reader.onload = () => {
          formik.setFieldValue("vehicle_image", file);
          setVehicleImage(reader.result);
        };
        reader.readAsDataURL(file);
      }
    },
  });

  const handleAddVehicle = (formValues) => {
    dispatch(showLoader("Loading please wait..."));

    const formData = new FormData();
    formData.append("vehicle_name", formValues.vehicle_name);
    formData.append("vehicle_no", formValues.vehicle_no);
    formData.append("seat", formValues.seat);
    formData.append("service_type_id", formValues.service_type_id);
    formData.append("vehicle_manufacturer", formValues.vehicle_manufacturer);
    formData.append("manufacturing_year", formValues.manufacturing_year);
    formData.append("vehicle_brand", formValues.vehicle_brand);
    formData.append("vehicle_color", formValues.vehicle_color);
    formData.append("insurance_no", formValues.insurance_no);
    formData.append("insurance_exp", formValues.insurance_exp);
    formData.append("status", formValues.status);
    formData.append("proprio_id", authUser.id);
    if (formValues.vehicle_image)
      formData.append("vehicle_image", formValues.vehicle_image);

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.status === 201 || res?.status === 200) {
        dispatch(
          showSnackbar({
            message: res?.data?.message || "Vehicle created successfully",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "success",
          })
        );
        formik.resetForm();
        setVehicleImage(null);
        handleClose();
      }
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      const messagesArray = err?.response?.data?.data || [];
      const message =
        messagesArray.length > 0
          ? messagesArray.map((i) => i.msg).join(", ")
          : err?.response?.data?.message || "Error while creating vehicle";
      dispatch(
        showSnackbar({
          message,
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    };

    VehicleApi.addVehicle(formData).then(onSuccess, onFailure);
  };

  const formik = useFormik({
    initialValues: {
      vehicle_name: "",
      vehicle_no: "",
      seat: "",
      service_type_id: "",
      vehicle_manufacturer: "",
      manufacturing_year: "",
      vehicle_brand: "",
      vehicle_color: "",
      insurance_no: "",
      insurance_exp: "",
      status: 1,
      vehicle_image: "",
    },
    validationSchema: yup.object({
      vehicle_name: yup.string().required("Car model is required").nullable(),
      vehicle_no: yup
        .string()
        .required("License plate number is required")
        .nullable(),
      seat: yup
        .number()
        .typeError("Must be a number")
        .required("Seat capacity is required")
        .min(1, "Minimum 1 seat")
        .nullable(),
      service_type_id: yup
        .string()
        .required("Service type is required")
        .nullable(),
      vehicle_manufacturer: yup
        .string()
        .required("Manufacturer is required")
        .nullable(),
      manufacturing_year: yup
        .string()
        .required("Manufacturing year is required")
        .nullable(),
      vehicle_brand: yup.string().required("Car brand is required").nullable(),
      vehicle_color: yup.string().required("Car color is required").nullable(),
    }),
    enableReinitialize: true,
    onSubmit: handleAddVehicle,
  });

  const vehicleTypes = [
    "SEDAN",
    "SUV",
    "HATCHBACK",
    "VAN",
    "TRUCK",
    "BIKE",
    "OTHER",
  ];

  const years = [];
  for (let y = new Date().getFullYear(); y >= 1990; y--) {
    years.push(String(y));
  }

  return (
    <>
      <Dialog
        open={openDialog}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        maxWidth={"lg"}
        aria-describedby="vehicle-create-dialog"
        className={classes.root}
      >
        <DialogTitle>
          <section className={classes.dialogTitleContainer}>
            <Typography variant="body1" className={classes.title}>
              Add New Vehicle
            </Typography>
            <IconButton aria-label="close" onClick={handleClose}>
              <CloseIcon className={classes.closeIcon} />
            </IconButton>
          </section>
        </DialogTitle>

        <DialogContent>
          {/* Vehicle Image — passenger-style avatar */}
          <Grid container>
            <Grid item xs={12}>
              <section className={classes.avatarContainer}>
                <div className={classes.avatarWrapper}>
                  <div className={classes.avatar}>
                    {vehicleImage ? (
                      <img
                        src={vehicleImage}
                        alt="vehicle"
                        className={classes.vehicle_img}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          borderRadius: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <DirectionsCarIcon style={{ fontSize: 56, color: "#aaa" }} />
                      </Box>
                    )}
                    <section
                      {...getRootProps()}
                      onClick={open}
                      className={classes.editIcon}
                    >
                      <input {...getInputProps()} />
                      <CameraAltOutlinedIcon />
                    </section>
                  </div>
                </div>
              </section>
            </Grid>
          </Grid>

          <Grid container>
            <section className={classes.container}>
              <Grid item xs={12} className={classes.detailsContainer}>
                <Grid container>
                  {/* Row 1: Car Model + License Plate */}
                  <Grid
                    item
                    xs={12}
                    md={6}
                    className={classes.textFieldWrapper}
                  >
                    <TextField
                      id="vehicle_name"
                      name="vehicle_name"
                      label="Car Model *"
                      value={formik.values.vehicle_name}
                      onChange={formik.handleChange}
                      error={
                        formik.errors.vehicle_name &&
                        formik.touched.vehicle_name
                      }
                      helperText={
                        formik.errors.vehicle_name &&
                        formik.touched.vehicle_name &&
                        formik.errors.vehicle_name
                      }
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={6}
                    className={classes.textFieldWrapper}
                  >
                    <TextField
                      id="vehicle_no"
                      name="vehicle_no"
                      label="License Plate No *"
                      value={formik.values.vehicle_no}
                      onChange={formik.handleChange}
                      error={
                        formik.errors.vehicle_no && formik.touched.vehicle_no
                      }
                      helperText={
                        formik.errors.vehicle_no &&
                        formik.touched.vehicle_no &&
                        formik.errors.vehicle_no
                      }
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                  </Grid>

                  {/* Row 2: Manufacturer + Brand */}
                  <Grid
                    item
                    xs={12}
                    md={6}
                    className={classes.textFieldWrapper}
                  >
                    <TextField
                      id="vehicle_manufacturer"
                      name="vehicle_manufacturer"
                      label="Car Manufacturer Name *"
                      value={formik.values.vehicle_manufacturer}
                      onChange={formik.handleChange}
                      error={
                        formik.errors.vehicle_manufacturer &&
                        formik.touched.vehicle_manufacturer
                      }
                      helperText={
                        formik.errors.vehicle_manufacturer &&
                        formik.touched.vehicle_manufacturer &&
                        formik.errors.vehicle_manufacturer
                      }
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={6}
                    className={classes.textFieldWrapper}
                  >
                    <TextField
                      id="vehicle_brand"
                      name="vehicle_brand"
                      label="Car Brand *"
                      value={formik.values.vehicle_brand}
                      onChange={formik.handleChange}
                      error={
                        formik.errors.vehicle_brand &&
                        formik.touched.vehicle_brand
                      }
                      helperText={
                        formik.errors.vehicle_brand &&
                        formik.touched.vehicle_brand &&
                        formik.errors.vehicle_brand
                      }
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                  </Grid>

                  {/* Row 3: Manufacturing Year + Seat Capacity */}
                  <Grid
                    item
                    xs={12}
                    md={6}
                    className={classes.textFieldWrapper}
                  >
                    <TextField
                      id="manufacturing_year"
                      name="manufacturing_year"
                      label="Manufacturing Year *"
                      value={formik.values.manufacturing_year}
                      onChange={formik.handleChange}
                      select
                      error={
                        formik.errors.manufacturing_year &&
                        formik.touched.manufacturing_year
                      }
                      helperText={
                        formik.errors.manufacturing_year &&
                        formik.touched.manufacturing_year &&
                        formik.errors.manufacturing_year
                      }
                      fullWidth
                      variant="outlined"
                      size="small"
                    >
                      {years.map((y) => (
                        <MenuItem key={y} value={y}>
                          {y}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={6}
                    className={classes.textFieldWrapper}
                  >
                    <TextField
                      id="seat"
                      name="seat"
                      label="Passenger Seat Capacity *"
                      type="number"
                      value={formik.values.seat}
                      onChange={formik.handleChange}
                      error={formik.errors.seat && formik.touched.seat}
                      helperText={
                        formik.errors.seat &&
                        formik.touched.seat &&
                        formik.errors.seat
                      }
                      fullWidth
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 1 }}
                    />
                  </Grid>

                  {/* Row 4: Service Type + Color */}
                  <Grid
                    item
                    xs={12}
                    md={6}
                    className={classes.textFieldWrapper}
                  >
                    <TextField
                      id="service_type_id"
                      name="service_type_id"
                      label="Service Type *"
                      value={formik.values.service_type_id}
                      onChange={formik.handleChange}
                      select
                      error={
                        formik.errors.service_type_id &&
                        formik.touched.service_type_id
                      }
                      helperText={
                        formik.errors.service_type_id &&
                        formik.touched.service_type_id &&
                        formik.errors.service_type_id
                      }
                      fullWidth
                      variant="outlined"
                      size="small"
                    >
                      {serviceTypes.map((st) => (
                        <MenuItem key={st._id} value={st._id}>
                          {st.serviceName || st.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={6}
                    className={classes.textFieldWrapper}
                  >
                    <TextField
                      id="vehicle_color"
                      name="vehicle_color"
                      label="Car Color *"
                      value={formik.values.vehicle_color}
                      onChange={formik.handleChange}
                      error={
                        formik.errors.vehicle_color &&
                        formik.touched.vehicle_color
                      }
                      helperText={
                        formik.errors.vehicle_color &&
                        formik.touched.vehicle_color &&
                        formik.errors.vehicle_color
                      }
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                  </Grid>

                  {/* Row 5: Insurance No + Insurance Exp */}
                  <Grid
                    item
                    xs={12}
                    md={6}
                    className={classes.textFieldWrapper}
                  >
                    <TextField
                      id="insurance_no"
                      name="insurance_no"
                      label="Car Insurance Number"
                      value={formik.values.insurance_no}
                      onChange={formik.handleChange}
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={6}
                    className={classes.textFieldWrapper}
                  >
                    <TextField
                      id="insurance_exp"
                      name="insurance_exp"
                      label="Insurance Expiry Date"
                      type="date"
                      value={formik.values.insurance_exp}
                      onChange={formik.handleChange}
                      fullWidth
                      variant="outlined"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  {/* Row 6: Status */}

                  <Grid
                    item
                    xs={12}
                    md={6}
                    className={classes.textFieldWrapper}
                  >
                    <TextField
                      id="status"
                      name="status"
                      label="Status"
                      value={formik.values.status}
                      onChange={formik.handleChange}
                      select
                      fullWidth
                      variant="outlined"
                      size="small"
                    >
                      <MenuItem value={1}>Active</MenuItem>
                      <MenuItem value={0}>Inactive</MenuItem>
                    </TextField>
                  </Grid>

                  {/* Submit Button */}
                  <Grid
                    item
                    xs={12}
                    className={classes.sbtBtnWrapper}
                  >
                    <Button
                      variant="contained"
                      onClick={formik.handleSubmit}
                      startIcon={<DirectionsCarIcon />}
                    >
                      Add Vehicle
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </section>
          </Grid>
        </DialogContent>

        <Divider />
        <DialogActions>
          <section className={classes.dialogActions}>
            <Button onClick={handleClose}>Close</Button>
          </section>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VehicleCreate;
